import { useEffect, useRef } from "react";
import { useTimer } from "react-timer-hook";
import { TimerOutlined } from "@mui/icons-material";
import { Box, Grid, InputBase, styled, Typography } from "@mui/material";

const InputElement = styled("input")(({ theme }) => ({
  width: "50px",
  height: "50px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  alignSelf: "center",
  justifySelf: "center",
  fontSize: "24px",
  fontFamily: "inherit",
  fontWeight: 600,
  lineHeight: 1.5,
  padding: "8px 0",
  borderRadius: "8px",
  textAlign: "center",
  color: theme.palette.common.black,
  background: theme.palette.grey[200],
  border: `1px solid ${theme.palette.primary.main}`,
  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
  transition: "all 0.2s ease-in-out",

  "&:hover": {
    borderColor: theme.palette.primary.dark,
  },
  "&:focus": {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 3px ${theme.palette.primary.main}44`,
    outline: "none",
  },

  [theme.breakpoints.down(1100)]: {
    width: "45px",
    height: "45px",
    fontSize: "22px",
  },
  [theme.breakpoints.down(990)]: {
    width: "40px",
    height: "40px",
    fontSize: "20px",
  },
  [theme.breakpoints.down(900)]: {
    width: "50px",
    height: "50px",
    fontSize: "24px",
  },
}));

export const OtpInput = (props) => {
  const {
    separator,
    length,
    value,
    onChange,
    piece = 1,
    sx,
    time,
    onTimeExpire,
    onComplete,
  } = props;

  const { seconds, minutes, restart, isRunning } = useTimer({
    expiryTimestamp: new Date(),
    onExpire: onTimeExpire,
  });

  useEffect(() => {
    if (time) {
      const currentDate = new Date();
      currentDate.setSeconds(currentDate.getSeconds() + time);
      restart(currentDate);
    }
  }, [time]);

  useEffect(() => {
    if (value.length === length && onComplete) {
      onComplete(value);
    }
  }, [value]);

  const inputRefs = useRef(new Array(length).fill(null));

  const focusInput = (targetIndex) => {
    const targetInput = inputRefs.current[targetIndex];
    targetInput.click();
    targetInput.focus();
  };

  const selectInput = (targetIndex) => {
    const targetInput = inputRefs.current[targetIndex];
    targetInput.click();
    targetInput.focus();
  };

  const handleKeyDown = (event, currentIndex) => {
    switch (event.key) {
      case " ":
        event.preventDefault();
        break;
      case "ArrowLeft":
        event.preventDefault();
        if (currentIndex > 0) {
          focusInput(currentIndex - 1);
          selectInput(currentIndex - 1);
        }
        break;
      case "ArrowRight":
        event.preventDefault();
        if (currentIndex < length - 1) {
          focusInput(currentIndex + 1);
          selectInput(currentIndex + 1);
        }
        break;
      case "Delete":
        event.preventDefault();
        onChange((prevOtp) => {
          return prevOtp.slice(0, currentIndex) + prevOtp.slice(currentIndex + 1);
        });
        break;
      case "Backspace":
        event.preventDefault();
        if (currentIndex > 0) {
          focusInput(currentIndex - 1);
          selectInput(currentIndex - 1);
        }
        onChange((prevOtp) => {
          return prevOtp.slice(0, currentIndex) + prevOtp.slice(currentIndex + 1);
        });
        break;
      default:
        break;
    }
  };

  const handleChange = (event, currentIndex) => {
    const currentValue = event.target.value;
    let indexToEnter = 0;
    while (indexToEnter <= currentIndex) {
      if (inputRefs.current[indexToEnter].value && indexToEnter < currentIndex) {
        indexToEnter += 1;
      } else {
        break;
      }
    }

    onChange((prev) => {
      const otpArray = prev.split("");
      const lastValue = currentValue[currentValue.length - 1];
      otpArray[indexToEnter] = lastValue;
      return otpArray.join("");
    });

    if (currentValue !== "" && currentIndex < length - 1) {
      focusInput(currentIndex + 1);
    }
  };

  const handleClick = (currentIndex) => {
    selectInput(currentIndex);
  };

  const handlePaste = (event, currentIndex) => {
    event.preventDefault();
    const clipboardData = event.clipboardData;
    if (clipboardData.types.includes("text/plain")) {
      let pastedText = clipboardData.getData("text/plain");
      pastedText = pastedText.substring(0, length).trim();

      let indexToEnter = 0;
      while (indexToEnter <= currentIndex) {
        if (inputRefs.current[indexToEnter].value && indexToEnter < currentIndex) {
          indexToEnter += 1;
        } else {
          break;
        }
      }

      const otpArray = value.split("");
      for (let i = indexToEnter; i < length; i += 1) {
        otpArray[i] = pastedText[i - indexToEnter] ?? " ";
      }

      onChange(otpArray.join(""));
    }
  };

  return (
    <Grid container>
      <Grid
        direction="row"
        display="flex"
        item
        justifyContent="center"
        sx={{ py: 2, ...sx }}
        xs={12}
      >
        {new Array(length).fill(null).map((_, index) => (
          <Box
            key={index}
            sx={{ display: "flex", alignItems: "center", ...(index !== length - 1 && { mr: 1 }) }}
          >
            <InputBase
              autoFocus={index === 0}
              inputRef={(ele) => {
                inputRefs.current[index] = ele;
              }}
              onChange={(event) => handleChange(event, index)}
              onClick={() => handleClick(index)}
              onKeyDown={(event) => handleKeyDown(event, index)}
              onPaste={(event) => handlePaste(event, index)}
              slots={{
                input: InputElement,
              }}
              value={value[index] ?? ""}
            />
            {separator && index !== length - 1 && (index + 1) % (length / piece) === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: 30,
                  textAlign: "center",
                  fontSize: 30,
                }}
              >
                {separator}
              </Box>
            ) : (
              <Box width={0} />
            )}
          </Box>
        ))}
      </Grid>
      {isRunning && (
        <Grid item xs={12}>
          <Typography
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "end",
              justifyContent: "end",
              lineHeight: "22px",
              gap: "4px",
              svg: { width: "20px" },
              mt: 0.5,
              mr: 2,
            }}
            variant="subtitle1"
          >
            <TimerOutlined color="secondary" />
            {`${("0" + minutes).slice(-2)}:${("0" + seconds).slice(-2)}`}
          </Typography>
        </Grid>
      )}
    </Grid>
  );
};
